/**
 * Quantity Selector Component
 *
 * Reusable +/- quantity input control.
 */

import { Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuantitySelectorProps {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    className?: string;
}

export const QuantitySelector = ({
    value,
    onChange,
    min = 1,
    max = 99,
    size = 'md',
    disabled = false,
    className,
}: QuantitySelectorProps) => {
    const handleDecrement = () => {
        if (value > min) {
            onChange(value - 1);
        }
    };

    const handleIncrement = () => {
        if (value < max) {
            onChange(value + 1);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseInt(e.target.value, 10);
        if (!isNaN(newValue) && newValue >= min && newValue <= max) {
            onChange(newValue);
        }
    };

    const sizeClasses = {
        sm: {
            container: 'h-7',
            button: 'w-7 h-7',
            input: 'w-8 text-xs',
            icon: 'h-3 w-3',
        },
        md: {
            container: 'h-9',
            button: 'w-9 h-9',
            input: 'w-10 text-sm',
            icon: 'h-4 w-4',
        },
        lg: {
            container: 'h-11',
            button: 'w-11 h-11',
            input: 'w-12 text-base',
            icon: 'h-5 w-5',
        },
    };

    const sizes = sizeClasses[size];

    return (
        <div
            className={cn(
                'inline-flex items-center border border-border rounded-md',
                sizes.container,
                disabled && 'opacity-50',
                className
            )}
        >
            <button
                type="button"
                onClick={handleDecrement}
                disabled={disabled || value <= min}
                className={cn(
                    'flex items-center justify-center border-r border-border text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors delight-press',
                    sizes.button
                )}
                aria-label="Decrease quantity"
            >
                <Minus className={sizes.icon} />
            </button>
            <input
                type="number"
                value={value}
                onChange={handleInputChange}
                disabled={disabled}
                min={min}
                max={max}
                className={cn(
                    'text-center border-0 focus:ring-0 focus:outline-none font-medium text-foreground',
                    sizes.input,
                    '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                )}
                aria-label="Quantity"
            />
            <button
                type="button"
                onClick={handleIncrement}
                disabled={disabled || value >= max}
                className={cn(
                    'flex items-center justify-center border-l border-border text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors delight-press',
                    sizes.button
                )}
                aria-label="Increase quantity"
            >
                <Plus className={sizes.icon} />
            </button>
        </div>
    );
};

export default QuantitySelector;
